class PromoPopup extends HTMLElement {
	constructor() {
		super()
		this.dialog = this.querySelector('.promo-popup__dialog')
		this.autoOpen = this.dataset.autoOpen === 'true'
		this.delay = parseInt(this.dataset.delay, 10) || 0
		this.frequency = this.dataset.frequency || 'visitor'
		this.sectionId = this.dataset.sectionId
		this.storageKey = `promo-popup:${this.sectionId}`
		this.timer = null
	}

	connectedCallback() {
		this.querySelectorAll('[data-promo-close]').forEach((el) =>
			el.addEventListener('click', () => this.close()),
		)
		this.addEventListener('keydown', (event) => {
			if (event.key === 'Escape') this.close()
		})

		// Click outside the dialog (host or overlay) closes the popup.
		this.addEventListener('click', (event) => {
			if (event.target === this) this.close()
		})

		document
			.querySelectorAll(`[data-promo-open="${this.sectionId}"]`)
			.forEach((el) => el.addEventListener('click', () => this.open()))

		this.form = this.querySelector('.promo-popup__form')
		if (this.form) this.setupForm()

		if (window.Shopify && window.Shopify.designMode) {
			this.bindEditorEvents()
			return
		}

		if (!this.autoOpen || this.dismissed()) return

		// Wait for the entrance gate (sections/enter.liquid toggles body.st_load)
		// to be dismissed before starting the countdown.
		if (document.body.classList.contains('st_load')) {
			this.waitForEntrance()
		} else {
			this.scheduleAutoOpen()
		}
	}

	waitForEntrance() {
		const observer = new MutationObserver(() => {
			if (!document.body.classList.contains('st_load')) {
				observer.disconnect()
				this.scheduleAutoOpen()
			}
		})
		observer.observe(document.body, {
			attributes: true,
			attributeFilter: ['class'],
		})
	}

	scheduleAutoOpen() {
		this.timer = setTimeout(() => this.open(), this.delay * 1000)
	}

	bindEditorEvents() {
		document.addEventListener('shopify:section:select', (event) => {
			if (event.detail.sectionId === this.sectionId) this.open()
		})
		document.addEventListener('shopify:section:deselect', (event) => {
			if (event.detail.sectionId === this.sectionId) this.close()
		})
	}

	storage() {
		try {
			if (this.frequency === 'session') return window.sessionStorage
			if (this.frequency === 'visitor') return window.localStorage
		} catch (error) {
			return null
		}
		return null
	}

	dismissed() {
		const store = this.storage()
		try {
			return store ? store.getItem(this.storageKey) === '1' : false
		} catch (error) {
			return false
		}
	}

	remember() {
		const store = this.storage()
		if (!store) return
		try {
			store.setItem(this.storageKey, '1')
		} catch (error) {
			/* storage unavailable (private mode) — ignore */
		}
	}

	setupForm() {
		// Submit the customer form into a hidden iframe so the page never
		// navigates away. Progressive enhancement: with no JS the form just
		// submits normally and the page reloads to the success URL.
		const frameName = `promo-popup-frame-${this.sectionId}`
		const frame = document.createElement('iframe')
		frame.name = frameName
		frame.title = ''
		frame.setAttribute('aria-hidden', 'true')
		frame.setAttribute('tabindex', '-1')
		frame.style.display = 'none'
		this.appendChild(frame)

		this.frame = frame
		this.pendingSubmit = false
		this.form.target = frameName

		this.frame.addEventListener('load', () => this.onFrameLoad())
		this.form.addEventListener('submit', () => this.onSubmit())
	}

	onSubmit() {
		const errorEl = this.form.querySelector('[data-promo-error]')
		if (errorEl) {
			errorEl.hidden = true
			errorEl.textContent = ''
		}
		const button = this.form.querySelector('button[type="submit"]')
		if (button) button.disabled = true
		this.pendingSubmit = true
	}

	onFrameLoad() {
		// The iframe fires 'load' once when first inserted (about:blank) — ignore
		// that and only react after an actual submission.
		if (!this.pendingSubmit) return
		this.pendingSubmit = false

		const button = this.form.querySelector('button[type="submit"]')
		if (button) button.disabled = false
		const errorEl = this.form.querySelector('[data-promo-error]')

		let succeeded = false
		try {
			const search = this.frame.contentWindow.location.search
			succeeded = search.indexOf('customer_posted=true') !== -1
		} catch (error) {
			// Cross-origin (shouldn't happen on-store) — assume submitted.
			succeeded = true
		}

		if (succeeded) {
			this.showSuccess()
			return
		}

		if (errorEl) {
			errorEl.textContent = 'Please enter a valid email address.'
			errorEl.hidden = false
		}
	}

	showSuccess() {
		const wrapper = this.form.closest('[data-promo-email-success]')
		const message =
			(wrapper && wrapper.dataset.promoEmailSuccess) ||
			'Thanks for signing up!'
		const success = document.createElement('p')
		success.className = 'promo-popup__success'
		success.setAttribute('tabindex', '-1')
		success.textContent = message
		this.form.replaceChildren(success)
		success.focus()
	}

	open() {
		if (this.hasAttribute('open')) return
		if (this.timer) clearTimeout(this.timer)
		this.setAttribute('open', '')
		document.body.classList.add('overflow-hidden')
		if (typeof window.trapFocus === 'function')
			window.trapFocus(this.dialog)
		this.remember()
	}

	close() {
		if (this.timer) clearTimeout(this.timer)
		if (!this.hasAttribute('open')) return
		this.removeAttribute('open')
		document.body.classList.remove('overflow-hidden')
		if (typeof window.removeTrapFocus === 'function')
			window.removeTrapFocus()
		this.remember()
	}
}

if (!customElements.get('promo-popup')) {
	customElements.define('promo-popup', PromoPopup)
}
